import { JobStatus, modules } from '@veritone/glc-redux';
import { Action, Dispatch, ThunkAction } from '@reduxjs/toolkit';
import callGraphQLApi from '@helpers/callGraphQLApi';
import { DATABASE_UPDATE_DELAY } from '@cbsa-modules/mainPage/services';
import {
  lookupLatestCaseSchemaId,
  lookupLatestTdoLockSchemaId,
} from '@cbsa-modules/universal'; // TODO why are these funciton inside cbsa app?
import {
  CREATE_NEW_FOLDER_SUCCESS,
  CREATE_NEW_FOLDER_FAILURE,
  SEARCH_CASES_AND_FOLDERS_FAILURE,
  DELETE_FOLDER_REQUEST,
  SEARCH_CASES_AND_FOLDERS_INIT,
  FETCH_LEGACY_MEDIAS_INIT,
  FETCH_LEGACY_MEDIAS_SUCCESS,
  FETCH_LEGACY_MEDIAS_FAILURE,
  DELETE_MEDIA_FILE_SDO_FAILURE,
  DELETE_MEDIA_FILE_SDO_SUCCESS,
  FETCH_TDO_DETAIL_FOR_SDOS_SUCCESS,
  FETCH_TDO_DETAIL_FOR_SDOS_FAILURE,
  TDO_IN_USE,
  DELETE_FOLDER_FAILURE,
  DELETE_FOLDER_INIT,
  SOFT_DELETE_FOLDER_SUCCESS,
  SOFT_DELETE_FOLDER_FAILURE,
  actionMoveFolderRequest,
  CHECK_EMPTY_FOLDER_REQUEST,
  CHECK_EMPTY_FOLDER_SUCCESS,
  CHECK_EMPTY_FOLDER_FAILURE,
  LINK_FOLDER_AND_CASE_SDO,
  LINK_FOLDER_AND_CASE_SDO_SUCCESS,
  LINK_FOLDER_AND_CASE_SDO_FAILURE,
  actionSetSearchResults,
  SET_FOLDER_PERMISSIONS,
  FETCH_ROOT_FOLDERS_SUCCESS,
  REFRESH_FILE_OUTPUTS_FAILURE,
  REFRESH_FILE_OUTPUTS_INIT,
  REFRESH_FILE_OUTPUTS_SUCCESS,
} from '@redact-modules/casemedia/actions';
import {
  FolderId,
  TDOId,
  TreeObjectId,
} from '@common/state/modules/universal/models/Brands';
import {
  FolderPermissionFlags,
  FolderStateType,
  SearchResultFile,
} from '@redact-modules/casemedia/store';
import {
  selectSearchText,
  selectSearchDefaultFolderLimit,
  selectSearchDefaultFileLimit,
  selectSearchDefaultOffset,
  selectSearchMoreFilesOffset,
  selectSearchMoreFoldersOffset,
  selectSearchMoreFilesAvailable,
  selectSearchMoreFoldersAvailable,
  selectIsSearchMoreResults,
  selectSearchScopeFolderId,
  selectSearchScopeTreeObjectId,
  selectSearchScopeIsCase,
  selectSearchBarScopeMode,
  selectSearchResults,
  selectSelectedFolders,
  buildSearchCacheKey,
  selectFolderPermissions,
  selectIsOlpEnabled,
} from '@redact-modules/casemedia/selectors';
import {
  FolderPathItem,
  resolveFolderMutationId,
  selectAdminRootFolder,
  sortFolderPath,
} from '@redact/state/modules/helper';
import { SYSTEM_FOLDER_REDACT_TRASH } from '@redact-modules/trash/constants';
import { actionPurgeTdoSideData } from '@redact-modules/trash/actions';
import { markTdosPurgeable } from '@redact-modules/trash/pendingPurge';
import {
  createNewFolderQuery,
  createNewFolderLinkQuery,
  createNewFolderSdoQuery,
  searchFolderAndCasesQuery,
  searchFolderByParentQuery,
  SearchFolderByParentQueryResponse,
  deleteSdoQuery,
  fetchLegacyMediasQuery,
  getUnassignedTdoDetailsQuery,
  CreateNewFolderQueryResponse,
  CreateNewFolderSdoQueryResponse,
  findTdoLockQuery,
  getChildFoldersQuery,
  getFilesByCaseIdQuery,
  DeleteSdoQueryResponse,
  SoftDeleteSdoQueryResponse,
  SearchFolderAndCasesQueryResponse,
  softDeleteSdoQuery,
  getTdoDetailsQuery,
  FetchLegacyMediasQueryResponse,
  createSoftDeletedFolderSdoQuery,
  CreateSoftDeletedSdoQueryResponse,
  fetchSubFoldersQuery,
  FetchSubFoldersQueryResponse,
  FetchCaseContentsQueryResponse,
  fetchCaseContentsQuery,
  fetchStalenessAssetsQuery,
  StalenessAssetsResponse,
  fetchMediaAssetsQuery,
  MediaAssetsResponse,
  deleteFolderOrCaseQuery,
  fetchRootFoldersQuery,
  FetchRootFoldersQueryResponse,
} from './queries';
import {
  ActionDeleteCaseSdoPayload,
  ActionDeleteMediaFileSdoPayload,
  ActionFetchFolderRequest,
  ActionFetchLegacyMediasRequest,
  ActionSoftDeleteCaseSdoPayload,
} from '@redact-modules/casemedia/models';
import {
  IN_REDACTION_TAG_KEY,
  UpdateTDOLockResponse,
} from '@common-modules/mediaDetails';
import { getRedactionStatusIdentity } from '@common-modules/mediaDetails/redactionStatuses';
import {
  FilesElasticSearchResult,
  Folder,
  TdoDetail,
  TdoSearchDetail,
  TdosDetailsResponse,
} from '@redact-modules/casemedia/models/search';
import { lookupLatestSchemaId } from '@redact-state/modules/mainPage/services';
import { MediaAssetRecord } from '@redact-modules/casemedia/mediaAssets';
import { fetchGraphQLApi } from '@helpers/callApi';
import { LOCK_REFRESH } from '@helpers/constants';
import { resolveFileType } from '@helpers/mediaFile';
import moment from 'moment';
import {
  arrayHasContents,
  guid,
  isObjectWithProperty,
  isObjectWithStringProperty,
} from '@common/shared/util';
import { enqueueSnackbar } from '@common/state/modules/snackbar';
import {
  selectFeatureFlags,
  selectRootFolderSmartSelection,
} from '@common/user-permissions';
import { selectV2FoldersEnabled } from '@common/user-onboarding/selectors/organization';
import { Head, NonEmptyArray } from 'ts-essentials';
import { uniq } from 'lodash';

const {
  auth: { selectOAuthToken, selectSessionToken },
  config: { getConfig },
  user: { selectUser, logout },
} = modules;

type Thunk<P, R = any> = (
  payload: P,
  ...args: any[]
) => ThunkAction<Promise<R | undefined>, any, unknown, Action>;

// <T>(query: string, variables: {}, actionTypes: [string, string, string]) =>
const NOOP = 'noop';
export const invokeGqlCall =
  <T>(
    query: string,
    variables: object,
    actionTypes: Head<Parameters<typeof callGraphQLApi<T>>>['actionTypes'],
    // Folder-listing reads select nested `contentTemplates { sdo { data } }`. A
    // folder whose content template points at an already-deleted SDO makes the
    // API answer with the valid records plus a `not_found` for that nested sdo;
    // without this flag callGraphQLApi treats the whole response as a failure and
    // the folder view breaks ("Failed to fetch folders"). Opt read paths into
    // partial-data tolerance so one dangling row can't blank the tree. Mutations
    // must NOT set this — a genuine not_found there should still fail.
    options?: { returnPartialDataOnNotFound?: boolean }
  ) =>
  async (dispatch: Dispatch, getState: () => any) =>
    await callGraphQLApi<T>({
      actionTypes,
      query,
      variables,
      dispatch,
      getState,
      returnPartialDataOnNotFound: options?.returnPartialDataOnNotFound,
    });

// Each action-type slot is typed as a SINGLE function (not a union of the two
// concrete creators) — calling a union of action creators trips "no compatible
// call signature". The meta type is borrowed from the creators themselves via
// `Parameters<>` so nothing new has to be exported from callGraphQLApi.
type RootFoldersSuccessAC = (
  payload: FetchRootFoldersQueryResponse,
  meta: NonNullable<Parameters<typeof FETCH_ROOT_FOLDERS_SUCCESS>[1]>,
  error?: false
) => Action;
type RootFoldersFailureAC = (
  payload: { message: string; name: string }[],
  meta: { variables: { limit: number; offset: number } },
  error?: boolean
) => Action;

export type RootFoldersActionTypes = readonly [
  init: string,
  success: RootFoldersSuccessAC,
  failure: RootFoldersFailureAC,
];

// A GraphQL error is scoped to a NESTED field when its `path` reaches below the
// top-level query field, e.g.
// ["rootFolders", 0, "childFolders", "records", 3, "contentTemplates", 0, "sdo"].
// Under OLP those are the per-object denials this read is meant to survive: the
// server's `@verifyAuthRoleAccess` directive throws `NotAllowed` from inside the
// nested field's own resolver, so the executor always records a deep path (the
// list-level `@authListFilter` never throws at all — it filters in the query).
// An error whose path IS the top-level field, or which carries no path (schema
// validation, auth rejection before execution), rejected the whole read.
const isNestedFieldError = (error: unknown) =>
  isObjectWithProperty(error, 'path') &&
  Array.isArray(error.path) &&
  error.path.length > 1;

// Runs the `listRootFolders` query directly (not through callGraphQLApi) so the
// success/failure decision is based on the presence of usable data, NOT on the
// names of any GraphQL errors. Under object-level permissions the query returns
// the accessible org root in `data` ALONGSIDE `not_allowed` errors (and `null`
// array elements) for objects the caller can't see; callGraphQLApi would treat
// that whole response as a failure and blank the folder view. Any response that
// carries usable `rootFolders` data — or whose only errors are nested per-object
// denials — is a success; only a blocking error with no usable data fails. The
// caller supplies the action-type tuple, so this one thunk serves both the
// initial fetch and the pagination ("load more" of the root's children).
// Because it bypasses callGraphQLApi it must reproduce the two guarantees that
// helper provided: a FAILURE dispatch on a rejected fetch (otherwise the view
// keeps its INIT spinner forever, and trash's root-folder race never settles),
// and logout() on an expired session. See VE-20286.
export const fetchRootFolders =
  (
    payload: ActionFetchFolderRequest,
    [INIT, SUCCESS, FAILURE]: RootFoldersActionTypes
  ) =>
  async (dispatch: Dispatch, getState: () => any) => {
    const { limit = 0, offset = 0 } = payload;
    const variables = { limit, offset };

    dispatch({ type: INIT });

    const oauthToken = selectOAuthToken(getState());
    const sessionToken = selectSessionToken(getState());
    const { apiRoot, graphQLEndpoint, veritoneAppId } =
      getConfig<Window['config']>(getState());

    let response;
    try {
      response = await fetchGraphQLApi<FetchRootFoldersQueryResponse>({
        endpoint: `${apiRoot}/${graphQLEndpoint}`,
        query: fetchRootFoldersQuery,
        variables,
        token: sessionToken || oauthToken,
        veritoneAppId,
      });
    } catch (e) {
      // Transport-level failure (offline, non-JSON 5xx) — there is no GraphQL
      // envelope to inspect. Shape it like a GraphQL error so the FAILURE
      // handler's `payload[0].message` snackbar still reads correctly.
      const message = isObjectWithStringProperty(e, 'message')
        ? e.message
        : 'Unknown Error';
      dispatch(
        FAILURE([{ message, name: 'fetch_error' }], { variables }, true)
      );
      return;
    }

    const meta = {
      variables,
      operationName: undefined,
      query: fetchRootFoldersQuery,
      _internalRequestId: guid(),
      _shouldTrackRequestsIndividually: false,
      response,
    };

    const errors = response.errors ?? [];
    // Nested per-object denials are tolerated; everything else blocks the read.
    const blockingErrors = errors.filter((e) => !isNestedFieldError(e));

    // An expired session rejects the whole request, so it always lands in
    // blockingErrors. callGraphQLApi used to trigger the re-auth for us.
    if (
      blockingErrors.some(
        (e) =>
          isObjectWithStringProperty(e, 'name') &&
          e.name === 'authentication_error'
      )
    ) {
      dispatch(FAILURE(blockingErrors, { variables }, true));
      // logout() is a thunk; nothing here awaits the re-auth redirect.
      void dispatch(logout());
      return;
    }

    if (response.data?.rootFolders || !blockingErrors.length) {
      if (errors.length) {
        // Don't swallow silently — a tolerated error is still worth seeing when
        // diagnosing a folder tree that renders with pieces missing.
        console.debug('listRootFolders returned partial data', errors);
      }
      // OLP returns `null` for sibling roots the caller can't access (the array
      // TYPE says non-null, but the runtime response can contain them). Strip
      // them ONCE here so every downstream consumer — root selection, pagination,
      // reducers — sees only real folders and needs no null handling.
      const rootFolders = (response.data?.rootFolders ?? []).filter(Boolean);
      dispatch(SUCCESS({ rootFolders }, meta));
      return response.data;
    }

    dispatch(FAILURE(blockingErrors, { variables }, true));
  };

export const fetchFolderPermissions =
  (folderIds: FolderId[]) =>
  async (dispatch: Dispatch, getState: () => any) => {
    // The OLP short-circuit lives HERE, not in the callers. Several places ask
    // for these verdicts (menu open, selection change) and a non-OLP org must
    // issue no permission traffic at all — one guard at the entry point is what
    // makes that true regardless of which caller is added next. The selectors
    // short-circuit on the same flag, so an empty cache is never consulted.
    if (!selectIsOlpEnabled(getState())) {
      return;
    }

    const cached = selectFolderPermissions(getState());
    const idsToCheck = uniq(folderIds.filter((id) => id && !(id in cached)));

    if (!idsToCheck.length) {
      return;
    }

    // Three aliases over one `hasPermissions` field: the folder-view actions map
    // onto three DIFFERENT aiWARE permissions (see FolderPermissionFlags), and
    // asking for [UPDATE, FILE, DELETE] in a single `permissions` list would
    // collapse them into one verdict per id and tell us nothing about which of
    // the three was granted. Aliasing keeps them separate in one round trip.
    const query = `
      query checkFolderPermissions(
        $resourceType: AuthResourceType!,
        $ids: [ID!]!
      ) {
        update: hasPermissions(
          resourceType: $resourceType
          ids: $ids
          permissions: [AIWARE_FOLDER_UPDATE]
        ) {
          hasPermission
          id
        }
        file: hasPermissions(
          resourceType: $resourceType
          ids: $ids
          permissions: [AIWARE_FOLDER_FILE]
        ) {
          hasPermission
          id
        }
        delete: hasPermissions(
          resourceType: $resourceType
          ids: $ids
          permissions: [AIWARE_FOLDER_DELETE]
        ) {
          hasPermission
          id
        }
      }
    `;

    try {
      type Verdicts = { hasPermission: boolean; id: string }[];
      const result = await callGraphQLApi<{
        update: Verdicts;
        file: Verdicts;
        delete: Verdicts;
      }>({
        actionTypes: [NOOP, NOOP, NOOP],
        query,
        variables: {
          resourceType: 'Folder',
          ids: idsToCheck,
        },
        dispatch,
        getState,
        bailout: undefined,
      });

      // Absent from a SUCCESSFUL response → not permitted. The server answered
      // and declined to grant this id, which under OLP is how inaccessible
      // resources surface (same omission behaviour documented for rootFolders
      // above). Matches checkTdoPermissions, which also defaults an empty
      // response to false.
      const granted = (verdicts: Verdicts | undefined, id: FolderId) =>
        verdicts?.find((p) => p.id === id)?.hasPermission ?? false;

      const permissionsById: Record<string, FolderPermissionFlags> = {};
      for (const id of idsToCheck) {
        permissionsById[id] = {
          canEdit: granted(result?.update, id),
          canMove: granted(result?.file, id),
          canDelete: granted(result?.delete, id),
        };
      }
      dispatch(SET_FOLDER_PERMISSIONS(permissionsById));
    } catch (e) {
      // Deliberately write NOTHING to the cache on failure. `idsToCheck` filters
      // on this same map, so caching any verdict here would make a single
      // transient error (offline blip, 5xx, a `not_allowed` sibling) permanent
      // for the rest of the session. Leaving the id absent is what makes the
      // gate self-healing: selectCanEditFolder reads an absent id as
      // NOT editable (fail-closed), so the folder is gated for now, and the next
      // menu open re-runs the check instead of being short-circuited by a cached
      // answer.
      console.debug('Error checking folder permissions', e);
    }
  };

// VE-26016: delete order is deliberate. The case FOLDER (which carries the
// content-template link) is deleted first; the case SDO is purged only AFTER
// DELETE_FOLDER_SUCCESS, by the deleteCaseSdoRequest saga. The previous order
// deleted the SDO first, so any interrupted delete (folder delete then fails, is
// blocked as a non-empty folder, or the tab closes) left the case pointing at a
// deleted SDO — a dangling reference that breaks the folder-view fetch
// ("Failed to fetch folders"). Removing the link first means a failure leaves at
// most a harmless orphaned SDO, never a dangling link. Mirrors the trash-side
// ordering fix (VE-25907).
/**
 * Ask the trash module to purge the per-TDO side data of files a folder/case
 * delete just removed (VE-28551).
 *
 * Takes the MUTATION RESPONSE rather than the requested ids: the batch is an
 * aliased `deleteTDO` per file, each echoing its own `{ id }`, and a partial
 * response is the normal case rather than an edge one — callGraphQLApi returns
 * `undefined` for a batch containing any `not_found`, and returns only the
 * aliases that resolved otherwise. Anything the server did not confirm deleted
 * keeps its side data; a stranded row is recoverable, a file whose provenance
 * was erased under it is not.
 */
const purgeSideDataForDeletedTdos = (
  dispatch: Dispatch,
  response: unknown
): void => {
  if (!response || typeof response !== 'object') {
    return;
  }
  const deleted = Object.values(response as Record<string, unknown>)
    .map((entry) =>
      entry && typeof entry === 'object'
        ? (entry as { id?: unknown }).id
        : undefined
    )
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
  if (deleted.length === 0) {
    return;
  }
  // Registered, not just dispatched: the watcher purges only ids it can claim
  // from here, so the action cannot be forged against a file nobody deleted.
  // See trash/pendingPurge.
  markTdosPurgeable(deleted);
  dispatch(actionPurgeTdoSideData({ tdoIds: deleted as unknown as TDOId[] }));
};

export const deleteCaseSdo =
  (payload: ActionDeleteCaseSdoPayload) => (dispatch: Dispatch) => {
    const { isCase } = payload;
    dispatch(DELETE_FOLDER_REQUEST({ ...payload, isCase }));
    // callIfFolderCaseIsUnlocked awaits this thunk; return a settled promise so
    // it matches the awaited-thunk contract without a needless async wrapper.
    return Promise.resolve();
  };

// Best-effort purge of a case's now-orphaned SDO, run only once its folder has
// been deleted (see deleteCaseSdoRequest saga). A failure here leaves a harmless
// orphaned SDO — nothing references it — so it must not surface as a user-facing
// error or block anything.
export const purgeCaseSdo =
  ({ sdoId }: Pick<ActionDeleteCaseSdoPayload, 'sdoId'>) =>
  async (dispatch: Dispatch, getState: () => any) => {
    const schemaId = await lookupLatestCaseSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    try {
      await callGraphQLApi<DeleteSdoQueryResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: deleteSdoQuery,
        variables: { sdoId, schemaId },
        dispatch,
        getState,
        throwOnError: true,
      });
    } catch (err) {
      if (isObjectWithProperty(err, 'message')) {
        console.log(err.message);
      }
    }
  };

export const softDeleteCaseSdo =
  (payload: ActionSoftDeleteCaseSdoPayload, trashFolderId: string) =>
  async (dispatch: Dispatch, getState: () => any) => {
    const schemaId = await lookupLatestCaseSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const { sdoId, data, treeObjectId, folderId } = payload;
    try {
      const response = await callGraphQLApi<SoftDeleteSdoQueryResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: softDeleteSdoQuery,
        variables: { sdoId, schemaId, data },
        dispatch,
        getState,
        throwOnError: true,
      });

      if (response) {
        // VE-21114: the caller normalises oldFolderPath to the v1 (self-inclusive)
        // shape via tdoFolderChain, so the parent is always the second-to-last
        // node in both v1 and v2.
        const oldParentId = data.oldFolderPath?.at(-2)?.id;
        dispatch(
          actionMoveFolderRequest({
            treeObjectId,
            oldParentTreeId: oldParentId as FolderId,
            newParentTreeId: trashFolderId as FolderId,
            // VE-21114: v2 moveFolder authorizes by folder ids.
            folderId,
            oldParentId: oldParentId as FolderId,
            newParentId: trashFolderId as FolderId,
            isRoot: false,
            suppressSnackbar: true,
          })
        );
        dispatch(
          SOFT_DELETE_FOLDER_SUCCESS({
            id: treeObjectId,
            folderName: data.caseName,
          })
        );
      }
    } catch (err) {
      if (isObjectWithProperty(err, 'message')) {
        console.log(err.message);
      }
      dispatch(
        SOFT_DELETE_FOLDER_FAILURE({
          id: treeObjectId,
          folderName: data.caseName,
        })
      );
    }
  };

export const createNewFolder =
  ({
    newFolderName,
    parentId,
    isCase,
    isRoot,
    parentIsCase,
  }: {
    newFolderName: string;
    parentId: FolderId;
    isCase: boolean;
    isRoot: boolean;
    // The PARENT container's type, passed through untouched to the success
    // action so the post-create refetch can pick the right query.
    parentIsCase: boolean;
  }) =>
  async (dispatch: Dispatch, getState: () => any) => {
    const schemaId = await lookupLatestCaseSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const currentTime = new Date().toISOString();
    // VE-21114: v2 authorizes createFolderContentTemplate via OLP against the
    // folder's `id`; the deprecated `treeObjectId` no longer resolves there and
    // the link fails with not_allowed (folder+SDO already created → the request
    // appears but is unlinked). Link by `id` under v2 (v1 still uses treeObjectId).
    const isV2Folders = selectV2FoldersEnabled(getState());

    try {
      /* Create the folder object */
      const { createFolder: newFolder } =
        await callGraphQLApi<CreateNewFolderQueryResponse>({
          actionTypes: [NOOP, NOOP, CREATE_NEW_FOLDER_FAILURE],
          query: createNewFolderQuery,
          variables: {
            parentId,
            newFolderName,
          },
          dispatch,
          getState,
          throwOnError: true,
        });

      if (isCase) {
        /* Create the case sdo */
        const {
          createStructuredData: { id: newSdoId },
        } = await callGraphQLApi<CreateNewFolderSdoQueryResponse>({
          actionTypes: [NOOP, NOOP, CREATE_NEW_FOLDER_FAILURE],
          query: createNewFolderSdoQuery,
          variables: {
            newFolderName,
            folderTreeObjectId: newFolder.treeObjectId,
            currentTime,
            schemaId: schemaId,
          },
          dispatch,
          getState,
          throwOnError: true,
        });

        /* Link the folder and the case sdo together */
        await callGraphQLApi({
          actionTypes: [
            LINK_FOLDER_AND_CASE_SDO,
            LINK_FOLDER_AND_CASE_SDO_SUCCESS,
            CREATE_NEW_FOLDER_FAILURE,
          ],
          query: createNewFolderLinkQuery,
          variables: {
            schemaId,
            folderId: resolveFolderMutationId(isV2Folders, {
              folderId: newFolder.id,
              treeObjectId: newFolder.treeObjectId,
            }),
            sdoId: newSdoId,
          },
          dispatch,
          getState,
          throwOnError: true,
        });
      }

      await new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, DATABASE_UPDATE_DELAY);
      });

      dispatch(
        CREATE_NEW_FOLDER_SUCCESS({
          newFolderName,
          parentId,
          isRoot,
          parentIsCase,
        })
      );
    } catch (err) {
      if (isObjectWithStringProperty(err, 'message')) {
        console.log(err.message);
      }
      dispatch(CREATE_NEW_FOLDER_FAILURE({ newFolderName }, undefined));
    }
  };

type FileSearchQueryOperator = 'wildcard' | 'query_string' | 'terms';

type FileSearchQueryCondition =
  | {
      field: string;
      operator: FileSearchQueryOperator;
      value: string;
    }
  | {
      field: string;
      operator: FileSearchQueryOperator;
      values: string[];
    }
  | {
      operator: 'or';
      conditions: FileSearchQueryCondition[];
    };

/**
 * Elasticsearch field the literal (wildcard) filename search matches against.
 *
 * `veritone-file.filename.sort` is a `keyword` field with a lowercase
 * normalizer — the whole filename stored as one un-analyzed token. The bare
 * `veritone-file.filename` is an analyzed `text` field and is NOT
 * interchangeable with it; see buildFilenameWildcardPattern below.
 */
export const FILENAME_SEARCH_FIELD = 'veritone-file.filename.sort';

/**
 * Analyzed `text` field, searched alone for letters/digits-only terms and
 * alongside FILENAME_SEARCH_FIELD otherwise. Matches the search words as
 * separate words, in any order, which the literal wildcard cannot (with limits
 * on partial words — see buildFilenameSearchCondition).
 */
export const FILENAME_TOKEN_SEARCH_FIELD = 'veritone-file.filename';

/**
 * Build the `wildcard` pattern that matches a filename substring.
 *
 * This line has been flipped twice, each flip closing one ticket and re-opening
 * another. Read this before "simplifying" it:
 *
 *   VE-9076  (#3172) → `veritone-file.filename.sort`, so commas stopped
 *                      breaking matches. This caused VE-16448.
 *   VE-16448 (#3648) → back to `veritone-file.filename`. This caused VE-26800.
 *
 * Neither field is correct alone, because the field was never the whole
 * problem — the operator was:
 *
 *   - `veritone-file.filename` is analyzed `text`, and core-search-server
 *     special-cases that exact field name (operator-builder.elastic.js) to
 *     replace every non-word character — commas, parens, dots, spaces, and any
 *     backslash we escaped with — by a space, then ANDs the fragments. A pasted
 *     filename is shredded and matches nothing. That is VE-26800.
 *   - `.sort` fixes that, but the condition also used `query_string`, which
 *     splits its input on whitespace. On a keyword field an interior bare term
 *     means "the filename EQUALS this", so any query containing a space matched
 *     nothing. That is VE-16448.
 *
 * So: keyword field (no server-side rewrite) + `wildcard` operator (no
 * query_string parser, therefore no whitespace splitting). The only special
 * characters left are `*`, `?` and the escape character `\` itself, so those
 * are the only ones needing escaping — every other character, comma and
 * parenthesis included, matches literally.
 *
 * `\` must be escaped along with the wildcards, not omitted as "rare": a query
 * ENDING in a backslash would otherwise emit `*abc\*`, where the trailing `\*`
 * escapes our own closing wildcard and silently matches nothing.
 *
 * Whitespace runs collapse to `*` so spacing in a pasted filename need not match
 * exactly. On its own that makes word ORDER significant: `samuel sarabia` will
 * not match `…SARABIA,_SAMUEL…`. buildFilenameSearchCondition covers that case
 * by OR-ing this pattern with a token search on the analyzed field.
 *
 * Callers guard against empty input (fetchCasesFolderSearch returns early).
 */
export const buildFilenameWildcardPattern = (searchText: string): string => {
  const pattern = searchText
    .toLowerCase()
    .trim()
    // A `*`, `?` or `\` the user typed is a literal character, not syntax.
    // Escaping `\` first is why this is one character class, not two passes.
    .replace(/[\\*?]/g, '\\$&')
    .replace(/\s+/g, '*');
  return `*${pattern}*`;
};

/**
 * Build the `query_string` value for the analyzed FILENAME_TOKEN_SEARCH_FIELD,
 * or `null` when the search text has no letters or digits to search for.
 *
 * core-search-server replaces every character outside `[\w^*]` with a space
 * for this field anyway. Doing the same client-side (and dropping `^` and `*`,
 * which query_string would read as boost / wildcard syntax) lets us see when
 * nothing is left: a bare `**` there would match every file, and inside the OR
 * in buildFilenameSearchCondition that would swamp the literal match.
 */
export const buildFilenameTokenQuery = (searchText: string): string | null => {
  const words = searchText
    .toLowerCase()
    // Not `\W`: `_` is a regex word character but the analyzer splits on it,
    // so keeping it leaves `jane_doe` as one literal wildcard that matches
    // nothing the `.sort` branch doesn't already find.
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return words ? `*${words}*` : null;
};

const LETTERS_AND_DIGITS_ONLY = /^[a-z0-9]+$/i;

/**
 * The filename condition for the file search. Which field(s) we search depends
 * on the kind of term — agreed on PR #3927 / VE-26800. "both" is an OR: a file
 * matching EITHER field is returned, so the result is the union of the two.
 *
 *   Term type                      e.g.                .filename  .sort         Send
 *   1. Letters/digits only         report, a           match      match         .filename
 *   2. Multiple words (spaces)     jane doe            match      MISS if the   both
 *                                                                 order or
 *   3. Special chars, no spaces    jane_doe, doe,jane  match      separators    both
 *                                                                 differ
 *   4. Spaces + special chars      doe, jane (1)       some MISS  match         both
 *      (pasted filenames)
 *   5. Accented letters            café                too broad  match         both
 *   6. Only special chars          ()  .,-  _          skipped    match         .sort
 *
 *   1. Both fields find the same files at the same speed (the analyzed field
 *      keeps each whitespace chunk whole, so a term with no separator fits in
 *      one). `.filename` alone, because files indexed before `.sort` existed
 *      may have no `.sort` value, and the OR costs ~0.2–0.75s per search.
 *   2./3. `.sort` matches literally, so word order and separators must match
 *      the filename (VE-16448): `jane doe` finds `jane_doe.mp4` but not
 *      `DOE,_JANE.mp4`. `.filename` splits on punctuation and underscores and
 *      matches the words in any order, with one limit: query_string turns
 *      `*jane doe*` into `*jane` AND `doe*`, so the FIRST word only matches the
 *      end of a word, the LAST only the start of one, and middle words must be
 *      whole words. `jane doe` and `jane_doe` find `DOE,_JANE`; `sam sara`
 *      does not find `SARABIA,_SAMUEL` on either field. Wrapping every word in
 *      `*…*` would lift that, at the cost of one more leading-wildcard scan
 *      per word.
 *   4. The server shreds a pasted name on `.filename` into fragments that may
 *      match nothing (VE-26800). `.sort` matches it literally.
 *   5. The server treats non-ASCII letters as separators on `.filename`
 *      (`café` → `caf`), so that branch over-matches (`cafeteria`, `caffeine`).
 *      Because "both" is a union, those extra hits stay in the results — this
 *      over-matching is accepted, not prevented. Sending `.sort` alone would
 *      avoid it but lose any-order matching for accented multi-word names.
 *   6. Nothing is left to search on `.filename`, and an empty query there
 *      matches every file (see buildFilenameTokenQuery).
 */
export const buildFilenameSearchCondition = (
  searchText: string
): FileSearchQueryCondition => {
  const wildcardCondition: FileSearchQueryCondition = {
    field: FILENAME_SEARCH_FIELD,
    operator: 'wildcard',
    value: buildFilenameWildcardPattern(searchText),
  };
  const tokenQuery = buildFilenameTokenQuery(searchText);
  if (tokenQuery === null) {
    return wildcardCondition;
  }
  const tokenCondition: FileSearchQueryCondition = {
    field: FILENAME_TOKEN_SEARCH_FIELD,
    operator: 'query_string',
    value: tokenQuery,
  };
  if (LETTERS_AND_DIGITS_ONLY.test(searchText.trim())) {
    return tokenCondition;
  }
  return {
    operator: 'or',
    conditions: [wildcardCondition, tokenCondition],
  };
};

// The file search runs leading-wildcard queries that can take seconds. Only the
// newest request's results are ever rendered, so a new request aborts the one
// still in flight instead of letting it keep running. The saga's takeLatest
// does not do this: it `put`s this thunk, which then runs on its own.
let inFlightFileSearch: AbortController | null = null;

export const fetchCasesFolderSearch: Thunk<object> =
  () => async (dispatch, getState) => {
    // TODO: We need to type return of getState() properly
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const state = getState();
    const searchText: string = selectSearchText(state).trim();
    if (!searchText) {
      return;
    }
    const searchDefaultFolderLimit = selectSearchDefaultFolderLimit(state);
    const searchDefaultFileLimit = selectSearchDefaultFileLimit(state);
    const searchDefaultOffset = selectSearchDefaultOffset(state);
    const searchMoreFoldersOffset = selectSearchMoreFoldersOffset(state);
    const searchMoreFilesOffset = selectSearchMoreFilesOffset(state);
    const searchMoreFilesAvailable = selectSearchMoreFilesAvailable(state);
    const searchMoreFoldersAvailable = selectSearchMoreFoldersAvailable(state);
    const isSearchMoreResults = selectIsSearchMoreResults(state);
    const searchFoldersOffset =
      isSearchMoreResults && searchMoreFoldersAvailable
        ? searchDefaultFolderLimit + searchMoreFoldersOffset
        : searchDefaultOffset;
    const searchFilesOffset =
      isSearchMoreResults && searchMoreFilesAvailable
        ? searchDefaultFileLimit + searchMoreFilesOffset
        : searchDefaultOffset;
    const token =
      modules.auth.selectOAuthToken(state) ||
      modules.auth.selectSessionToken(state);
    const { apiRoot } = modules.config.getConfig(state);
    const searchNames = searchText.split(/[()\-\s]+/).filter(Boolean);
    const searchScopeFolderId = selectSearchScopeFolderId(state);
    const searchScopeTreeObjectId = selectSearchScopeTreeObjectId(state);
    const searchScopeIsCase = selectSearchScopeIsCase(state);
    const useGlobalSearch = selectSearchBarScopeMode(state) === 'everything';
    const capturedScopeMode = selectSearchBarScopeMode(state);
    const capturedScopeFolderId = selectSearchScopeFolderId(state);
    const capturedSearchText = searchText;

    const isStaleSearchRequest = () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const latestState = getState();
      return (
        selectSearchText(latestState).trim() !== capturedSearchText ||
        selectSearchBarScopeMode(latestState) !== capturedScopeMode ||
        selectSearchScopeFolderId(latestState) !== capturedScopeFolderId
      );
    };

    try {
      let searchFoldersListPromise = null;
      if (
        !isSearchMoreResults ||
        (isSearchMoreResults && searchMoreFoldersAvailable)
      ) {
        if (!useGlobalSearch && searchScopeFolderId && !searchScopeIsCase) {
          searchFoldersListPromise =
            callGraphQLApi<SearchFolderByParentQueryResponse>({
              actionTypes: [SEARCH_CASES_AND_FOLDERS_INIT, NOOP, NOOP],
              query: searchFolderByParentQuery,
              variables: {
                folderId: searchScopeFolderId,
                limit: searchDefaultFolderLimit,
                offset: searchFoldersOffset,
                names: [...searchNames],
              },
              dispatch,
              getState,
              throwOnError: false,
              // Selects nested contentTemplates { sdo { data } }; tolerate a
              // dangling (deleted) SDO so one bad match can't drop the whole
              // folder search result (VE-26016).
              returnPartialDataOnNotFound: true,
            });
        } else if (useGlobalSearch || !searchScopeFolderId) {
          searchFoldersListPromise =
            callGraphQLApi<SearchFolderAndCasesQueryResponse>({
              actionTypes: [SEARCH_CASES_AND_FOLDERS_INIT, NOOP, NOOP],
              query: searchFolderAndCasesQuery,
              variables: {
                limit: searchDefaultFolderLimit,
                offset: searchFoldersOffset,
                names: [...searchNames],
              },
              dispatch,
              getState,
              throwOnError: false,
              // Selects nested contentTemplates { sdo { data } }; tolerate a
              // dangling (deleted) SDO so one bad match can't drop the whole
              // folder search result (VE-26016).
              returnPartialDataOnNotFound: true,
            });
        }
      }

      let searchFilesPromise = null;
      let fileSearchSignal: AbortSignal | null = null;
      if (
        !isSearchMoreResults ||
        (isSearchMoreResults && searchMoreFilesAvailable)
      ) {
        const fileSearchJson = {
          index: ['mine'],
          select: ['veritone-file'],
          limit: searchDefaultFileLimit,
          offset: searchFilesOffset,
          query: {
            operator: 'and',
            conditions: [] as FileSearchQueryCondition[],
          },
        };
        fileSearchJson.query.conditions.push(
          buildFilenameSearchCondition(searchText)
        );
        if (!useGlobalSearch && searchScopeTreeObjectId) {
          fileSearchJson.query.conditions.push({
            field: 'parentTreeObjectIds',
            operator: 'terms',
            values: [searchScopeTreeObjectId],
          });
        }
        dispatch(SEARCH_CASES_AND_FOLDERS_INIT(undefined));
        inFlightFileSearch?.abort();
        const fileSearchController = new AbortController();
        inFlightFileSearch = fileSearchController;
        fileSearchSignal = fileSearchController.signal;
        searchFilesPromise = fetch(
          `${apiRoot}/api/search/file_search_authtoken`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(fileSearchJson),
            signal: fileSearchSignal,
          }
        ).then((response) => {
          // A rejected query (bad request, expensive-query limits, 5xx) has an
          // error body with no `records`. Parsed as a result, it renders as
          // "No results" — the same symptom as VE-9076/16448/26800, so a
          // server failure would be indistinguishable from a missed match.
          if (!response.ok) {
            throw new Error(
              `File search failed: ${response.status} ${response.statusText}`
            );
          }
          return response.json() as Promise<FilesElasticSearchResult>;
        });
      }

      // VE-25873 Unit 1 — when the term is a 10-digit TDO ID, look the TDO up
      // globally (independent of scope) and merge it into results. Only on the
      // initial results set, never on "search more" paging (FR-1.1..FR-1.3).
      const isTdoIdSearch = !isSearchMoreResults && isTdoIdTerm(searchText);
      let tdoIdLookupPromise: Promise<
        TdosDetailsResponse | null | undefined
      > | null = null;
      if (isTdoIdSearch) {
        tdoIdLookupPromise = callGraphQLApi<TdosDetailsResponse>({
          actionTypes: [NOOP, NOOP, NOOP],
          query: getTdoDetailsQuery([searchText as TDOId], {
            includePrimaryAsset: true,
          }),
          variables: {},
          dispatch,
          getState,
          throwOnError: false,
          returnPartialDataOnNotFound: true,
        });
      }

      const [folderSettled, filesSettled, tdoIdSettled] =
        await Promise.allSettled([
          searchFoldersListPromise ?? Promise.resolve(null),
          searchFilesPromise ?? Promise.resolve(null),
          // The TDO-ID lookup must never fail the search — its rejection is
          // swallowed here so filename/folder results still render (NFR-1).
          tdoIdLookupPromise ?? Promise.resolve(null),
        ]);
      // Superseded by a newer search, which owns the in-progress state and the
      // results. isStaleSearchRequest can't tell: the newer search may use the
      // same text (e.g. Enter right after the debounce fired).
      if (fileSearchSignal?.aborted) {
        return;
      }
      // If a request actually errored (e.g. the file-search fetch rejected),
      // surface a failure instead of silently rendering an empty result set.
      if (
        folderSettled.status === 'rejected' ||
        filesSettled.status === 'rejected'
      ) {
        if (!isStaleSearchRequest()) {
          const reason: unknown =
            folderSettled.status === 'rejected'
              ? folderSettled.reason
              : filesSettled.status === 'rejected'
                ? filesSettled.reason
                : undefined;
          dispatch(
            SEARCH_CASES_AND_FOLDERS_FAILURE(
              isObjectWithStringProperty(reason, 'message')
                ? reason.message
                : undefined
            )
          );
        }
        return;
      }

      const searchedFolderList =
        folderSettled.status === 'fulfilled' ? folderSettled.value : null;
      const filesResults =
        filesSettled.status === 'fulfilled' ? filesSettled.value : null;

      let allTdosDetailsResponse = null;
      const filteredTdos: Array<TdoDetail> = [];
      const tdosWithFilePath = new Map<TDOId, TdoSearchDetail>();
      // TDOs living under the REDACT TRASH folder — excluded from results so
      // trashed files don't show up in search (matches Drive/Gmail behavior).
      const trashedTdoIds = new Set<string>();
      if (filesResults?.records && arrayHasContents(filesResults.records)) {
        // TODO: Is it correct to assume that mediaId -> string is a TODId?
        const allTdoIds: TDOId[] = filesResults.records.map(
          (f) => `${f.mediaId}`
        );
        const batchSize = 30;
        const batchFilePathCall = async (batchTdoIds: TDOId[]) => {
          if (arrayHasContents(batchTdoIds)) {
            const query = getTdoDetailsQuery(batchTdoIds);
            allTdosDetailsResponse = await callGraphQLApi<TdosDetailsResponse>({
              actionTypes: [NOOP, NOOP, NOOP],
              query,
              variables: {},
              dispatch,
              getState,
              throwOnError: false,
              returnPartialDataOnNotFound: true,
            });

            if (allTdosDetailsResponse) {
              for (const tdo of Object.values(allTdosDetailsResponse)) {
                if (!tdo?.id) {
                  continue;
                }
                // Drop trashed TDOs: don't record their path and remember the
                // id so their file-search record is filtered out below.
                if (tdo.folders?.some(isFolderUnderTrash)) {
                  trashedTdoIds.add(`${tdo.id}`);
                  continue;
                }
                if (tdo.folders?.length > 0) {
                  filteredTdos.push(tdo);
                }
                const folder = tdo.folders?.[0];
                const filePath = folder
                  ? concatenateNamesWithForwardSlash(folder)
                  : '';
                const status = getRedactionStatusFromTdo(tdo);
                const existing = tdosWithFilePath.get(tdo.id);
                tdosWithFilePath.set(tdo.id, {
                  filePath: filePath || existing?.filePath || '',
                  redactionStatus:
                    status?.redactionStatus ?? existing?.redactionStatus,
                  redactionStatusName:
                    status?.redactionStatusName ??
                    existing?.redactionStatusName,
                  // VE-25873 Unit 2 — immediate parent folder/request, so the
                  // result's breadcrumb can open it instead of the file.
                  parentFolderId: folder?.id ?? existing?.parentFolderId,
                  parentIsCase: Boolean(
                    (folder?.contentTemplates?.length ?? 0) > 0 ||
                      existing?.parentIsCase
                  ),
                });
              }
            }
          }
        };

        const numBatches = Math.ceil(allTdoIds.length / batchSize);
        for (let i = 0; i < numBatches; i++) {
          const startIndex = i * batchSize;
          const endIndex = startIndex + batchSize;
          const batchTdoIds = allTdoIds.slice(startIndex, endIndex);
          await batchFilePathCall(batchTdoIds);
        }

        if (!useGlobalSearch && searchScopeFolderId) {
          const scopedIds = new Set(
            filteredTdos
              .filter((tdo) =>
                isTdoWithinFolder(tdo, searchScopeFolderId as string)
              )
              .map((tdo) => tdo.id)
          );
          for (const tdoId of [...tdosWithFilePath.keys()]) {
            if (!scopedIds.has(tdoId)) {
              tdosWithFilePath.delete(tdoId);
            }
          }
        }
      }

      const scopedPathPrefix =
        !useGlobalSearch && searchScopeFolderId
          ? buildScopedPathPrefix(state)
          : undefined;

      const rawFolderRecords =
        !useGlobalSearch && searchScopeFolderId && !searchScopeIsCase
          ? ((searchedFolderList as SearchFolderByParentQueryResponse | null)
              ?.folder?.childFolders?.records ?? [])
          : useGlobalSearch || !searchScopeFolderId
            ? (pickRootAdminFolder(
                (searchedFolderList as SearchFolderAndCasesQueryResponse | null)
                  ?.rootFolders,
                selectRootFolderSmartSelection(state)
              )?.childFolders?.records ?? [])
            : [];
      const childFolders = rawFolderRecords.filter(
        ({ id, name, contentTemplates }) =>
          (useGlobalSearch ||
            !searchScopeFolderId ||
            id !== searchScopeFolderId) &&
          // Never surface the REDACT TRASH system folder in search results.
          name !== SYSTEM_FOLDER_REDACT_TRASH &&
          // Exclude requests/folders that have been moved to trash. A trashed
          // request can still be linked in the normal folder tree, so detect it
          // by the movedToTrashDateTime on its content-template SDO rather than
          // by parent folder. Check every template — a trashed folder may carry
          // more than one SDO and the trash one isn't necessarily first.
          !contentTemplates?.some(
            (ct) => ct?.sdo?.data?.movedToTrashDateTime
          ) &&
          searchNames.every((searchName) =>
            name?.toLowerCase().includes(searchName.toLowerCase())
          )
      );
      const foldersResults = {
        results: mapSearchFolderResults(
          childFolders as SearchFolderRecord[],
          scopedPathPrefix
        ),
        rawCount: rawFolderRecords.length,
        from: searchFoldersOffset,
        searchToken: token,
        totalResults: childFolders.length,
      };

      const scopedFilesResults =
        !useGlobalSearch && searchScopeFolderId && filesResults?.records
          ? {
              ...filesResults,
              records: filesResults.records.filter((r) =>
                tdosWithFilePath.has(`${r.mediaId}`)
              ),
            }
          : filesResults;

      // Exclude files that live in the REDACT TRASH folder from the results
      // (covers global search; scoped search already drops them via the
      // tdosWithFilePath filter above, since trashed TDOs aren't recorded).
      const visibleFileRecords = (scopedFilesResults?.records || []).filter(
        (r) => !trashedTdoIds.has(`${r.mediaId}`)
      );

      const apiSearchMoreFilesAvailable =
        (filesResults?.records?.length ?? 0) === searchDefaultFileLimit;

      // The abort check again: a same-text search may have superseded this one
      // while it awaited the TDO-details batches above.
      if (isStaleSearchRequest() || fileSearchSignal?.aborted) {
        return;
      }

      // VE-25873 Unit 1 — build the TDO-ID match (accessible, non-Trash only)
      // so it can be prepended ahead of all other results (FR-1.3..FR-1.5).
      const tdoIdResult: SearchResultFile | null = (() => {
        if (!isTdoIdSearch) {
          return null;
        }
        const resp =
          tdoIdSettled.status === 'fulfilled' ? tdoIdSettled.value : null;
        const tdo = resp ? Object.values(resp).find((t) => t?.id) : undefined;
        if (!tdo || tdo.folders?.some(isFolderUnderTrash)) {
          return null;
        }
        // VE-25873 — the TDO-ID lookup honors the scope toggle: when scoped to
        // a folder/request, only surface the TDO if it lives within that scope
        // (global only under "Everything"). Reversal of the original Q2=A.
        if (
          !useGlobalSearch &&
          searchScopeFolderId &&
          !isTdoWithinFolder(tdo, searchScopeFolderId as string)
        ) {
          return null;
        }
        const folder = tdo.folders?.[0];
        return {
          mediaId: Number(tdo.id),
          filename: tdo.name ?? `${tdo.id}`,
          createdDateTime: '',
          fullFilePath: folder ? concatenateNamesWithForwardSlash(folder) : '',
          redactionStatus: getRedactionStatusFromTdo(tdo)?.redactionStatus,
          redactionStatusName:
            getRedactionStatusFromTdo(tdo)?.redactionStatusName,
          parentFolderId: folder?.id as FolderId | undefined,
          parentIsCase: Boolean((folder?.contentTemplates?.length ?? 0) > 0),
          // Without the mimetype, PDFs matched by TDO ID open in the media
          // editor instead of the document editor — and the de-dupe below
          // drops the filename record that would otherwise carry it.
          // Resolve through the shared helper (primaryAsset.contentType →
          // veritoneFile mimetype/mimeType/fileType) rather than reading one
          // or two fields directly: primaryAsset(media) is null on some TDOs
          // and details' veritoneFile carries the MIME under a different key
          // depending on the ingestion path.
          mimetype: resolveFileType({
            primaryAsset: tdo.primaryAsset,
            details: parseTdoDetails(tdo.details),
          }),
          fileId: Number(tdo.id),
          matchedByTdoId: true,
        };
      })();

      const previousSearchResults = selectSearchResults(state);
      dispatch(
        actionSetSearchResults({
          searchResults: [
            ...(isSearchMoreResults ? previousSearchResults || [] : []),
            // TDO-ID match first (FR-1.3).
            ...(tdoIdResult ? [tdoIdResult] : []),
            ...(foldersResults?.results || []),
            // De-dupe: drop the filename record for the same TDO so it isn't
            // listed twice alongside the TDO-ID match.
            ...visibleFileRecords
              .filter(
                (currentRecord) =>
                  !tdoIdResult || currentRecord.mediaId !== tdoIdResult.mediaId
              )
              .map((currentRecord) => ({
                filename: currentRecord.metadata['veritone-file'].filename,
                mediaId: currentRecord.mediaId,
                createdDateTime: currentRecord.createdTime,
                fullFilePath:
                  tdosWithFilePath.get(`${currentRecord.mediaId}`)?.filePath ??
                  '',
                mimetype: currentRecord.metadata['veritone-file'].mimetype,
                redactionStatus: tdosWithFilePath.get(
                  `${currentRecord.mediaId}`
                )?.redactionStatus,
                redactionStatusName: tdosWithFilePath.get(
                  `${currentRecord.mediaId}`
                )?.redactionStatusName,
                // VE-25873 Unit 2 — parent folder/request for breadcrumb navigation.
                parentFolderId: tdosWithFilePath.get(`${currentRecord.mediaId}`)
                  ?.parentFolderId as FolderId | undefined,
                parentIsCase: tdosWithFilePath.get(`${currentRecord.mediaId}`)
                  ?.parentIsCase,
              })),
          ],
          searchMoreFilesOffset: searchFilesOffset,
          searchMoreFoldersOffset: searchFoldersOffset,
          searchMoreFilesAvailable: apiSearchMoreFilesAvailable,
          searchMoreFoldersAvailable:
            foldersResults?.rawCount === searchDefaultFolderLimit,
          cacheKey: buildSearchCacheKey(
            searchText,
            capturedScopeMode,
            capturedScopeFolderId
          ),
        })
      );
    } catch (err) {
      dispatch(
        SEARCH_CASES_AND_FOLDERS_FAILURE(
          isObjectWithStringProperty(err, 'message') ? err.message : undefined
        )
      );
    }
  };

// VE-25873 Unit 3 — resolve a TDO's owning case/folder (its "parent request").
// The editor's `caseId` IS a folder id (fetchCaseMedia queries
// `folder(id: caseId).childTDOs`), so the answer is one of the TDO's non-Trash
// parent folders; a folder carrying content templates is a case/request. When
// the URL already names a case, `preferFolderId` picks that parent when the
// TDO indeed lives under it (falling back to the first non-Trash parent when
// it doesn't — e.g. a stale link after the file was moved).
// Returns null when there is no accessible owning folder — and on ANY lookup
// error, so a failed lookup can never block the editor from opening (FR-3.5,
// NFR-1).
export const fetchTdoOwningCase: Thunk<
  { tdoId: TDOId; preferFolderId?: string },
  { caseId: FolderId; isCase: boolean } | null
> =
  ({ tdoId, preferFolderId }) =>
  async (dispatch, getState) => {
    try {
      const resp = await callGraphQLApi<TdosDetailsResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: getTdoDetailsQuery([tdoId]),
        variables: {},
        dispatch,
        getState,
        throwOnError: false,
        returnPartialDataOnNotFound: true,
      });
      const tdo = resp ? Object.values(resp).find((t) => t?.id) : undefined;
      const parents = (tdo?.folders ?? []).filter(
        (f) => f?.id && !isFolderUnderTrash(f)
      );
      const owningFolder =
        (preferFolderId && parents.find((f) => f.id === preferFolderId)) ||
        parents[0];
      if (!owningFolder?.id) {
        return null;
      }
      return {
        caseId: owningFolder.id as FolderId,
        isCase: (owningFolder.contentTemplates?.length ?? 0) > 0,
      };
    } catch {
      return null;
    }
  };

function concatenateNamesWithForwardSlash(folder: Folder): string {
  if (!folder.parent) {
    return '';
  }
  if (!folder.parent.parent) {
    return folder.name;
  }
  const parentPath = concatenateNamesWithForwardSlash(folder.parent);
  return parentPath ? `${parentPath}/${folder.name}` : folder.name;
}

// VE-25873 Unit 1 — a search term is treated as a candidate TDO ID when it is
// exactly a 10-digit number (see FR-1.1). Exported for unit tests.
export const isTdoIdTerm = (term: string): boolean =>
  /^\d{10}$/.test(term.trim());

// True when the TDO lives under the given folder id (directly or via any
// ancestor). Used to keep scoped search — including the VE-25873 TDO-ID
// lookup — limited to the currently-selected folder/request.
function isTdoWithinFolder(tdo: TdoDetail, scopeId: string): boolean {
  const checkFolder = (folder: Folder | undefined): boolean => {
    if (!folder) {
      return false;
    }
    if (folder.id === scopeId) {
      return true;
    }
    return checkFolder(folder.parent);
  };
  return tdo.folders.some((f) => checkFolder(f));
}

// A folder is "in trash" if it or any ancestor is the REDACT TRASH system
// folder. Used to keep trashed items out of search results.
function isFolderUnderTrash(folder: Folder | undefined): boolean {
  if (!folder) {
    return false;
  }
  if (folder.name === SYSTEM_FOLDER_REDACT_TRASH) {
    return true;
  }
  return isFolderUnderTrash(folder.parent);
}

function pickRootAdminFolder(
  rootFolders: SearchFolderAndCasesQueryResponse['rootFolders'] | undefined,
  smartSelection?: boolean
) {
  return selectAdminRootFolder(rootFolders, { smartSelection }) ?? null;
}

interface ParsedTdoDetails {
  tags?: unknown;
  // The MIME can live under any of these keys depending on the ingestion
  // path — resolveFileType checks them in order.
  veritoneFile?: { mimetype?: string; mimeType?: string; fileType?: string };
}

function parseTdoDetails(details: unknown): ParsedTdoDetails | null {
  if (typeof details === 'string') {
    try {
      return JSON.parse(details) as ParsedTdoDetails;
    } catch {
      return null;
    }
  }
  if (details && typeof details === 'object') {
    return details as ParsedTdoDetails;
  }
  return null;
}

function getRedactionStatusFromTdo(
  tdo: TdoDetail
): { redactionStatus: string; redactionStatusName: string } | undefined {
  const parsed = parseTdoDetails(tdo.details);
  if (!parsed || !isObjectWithProperty(parsed, 'tags')) {
    return undefined;
  }
  const rawTags: unknown = parsed.tags;
  if (!Array.isArray(rawTags)) {
    return undefined;
  }
  for (const tag of rawTags) {
    if (
      isObjectWithProperty(tag, 'value') &&
      tag.value === IN_REDACTION_TAG_KEY &&
      isObjectWithStringProperty(tag, 'redactionStatus')
    ) {
      // Identity = key when present (custom), else the stored name; keep the
      // frozen name for orphan display (VE-24546).
      const identity = getRedactionStatusIdentity(tag) ?? tag.redactionStatus;
      return {
        redactionStatus: identity,
        redactionStatusName: tag.redactionStatus,
      };
    }
  }
  return undefined;
}

function buildScopedPathPrefix(state: { [key: string]: unknown }): string[] {
  const selectedFolders = selectSelectedFolders(
    state as Parameters<typeof selectSelectedFolders>[0]
  );
  const parts: string[] = [];
  selectedFolders.forEach((folder) => {
    if (folder.name) {
      parts.push(folder.name);
    }
  });
  return parts;
}

function buildSearchFolderBreadcrumb(
  folderPath: FolderPathItem[] | undefined,
  folderName: string,
  scopedPathPrefix?: string[]
): string {
  if (folderPath?.length) {
    const sorted = sortFolderPath(folderPath);
    const names = sorted.map((folder) => folder.name).filter(Boolean);
    const pathWithoutRoot = names.length > 1 ? names.slice(1) : names;
    if (
      !pathWithoutRoot.length ||
      pathWithoutRoot[pathWithoutRoot.length - 1] !== folderName
    ) {
      pathWithoutRoot.push(folderName);
    }
    return pathWithoutRoot.join(' > ');
  }
  if (scopedPathPrefix?.length) {
    return [...scopedPathPrefix, folderName].join(' > ');
  }
  return folderName;
}

interface SearchFolderRecord {
  id?: string;
  name?: string;
  createdDateTime?: string;
  contentTemplates?: FolderStateType['contentTemplates'];
}

const mapSearchFolderResults = (
  childFolders: SearchFolderRecord[] | undefined,
  scopedPathPrefix?: string[]
) => {
  const folderList = (childFolders || [])
    .map(
      ({ id: folderTreeObjectId, name, createdDateTime, contentTemplates }) => {
        if (!name || !createdDateTime || !folderTreeObjectId) {
          return null;
        }
        return {
          caseName: name,
          createdDateTime,
          folderTreeObjectId: folderTreeObjectId as FolderId,
          contentTemplates,
          fullFolderPath: buildSearchFolderBreadcrumb(
            undefined,
            name,
            scopedPathPrefix
          ),
        };
      }
    )
    .filter((folder) => !!folder);
  return folderList || [];
};

export const fetchLegacyMedias =
  (payload: ActionFetchLegacyMediasRequest) =>
  async (dispatch: Dispatch, getState: () => any) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    return await callGraphQLApi<FetchLegacyMediasQueryResponse>({
      actionTypes: [
        FETCH_LEGACY_MEDIAS_INIT,
        FETCH_LEGACY_MEDIAS_SUCCESS,
        FETCH_LEGACY_MEDIAS_FAILURE,
      ],
      query: fetchLegacyMediasQuery,
      variables: { schemaId, ...payload },
      dispatch,
      getState,
    });
  };

export interface ActionSoftDeleteFolderPayload {
  folderId: FolderId;
  folderName: string;
  treeObjectId: TreeObjectId;
  trashRetentionDays: number;
  oldFolderPath: FolderPathItem[];
  movedToTrashByName: string;
  movedToTrashById: string;
}

export const softDeleteFolder =
  (payload: ActionSoftDeleteFolderPayload, trashFolderId: string) =>
  async (dispatch: Dispatch, getState: () => any) => {
    const schemaId = await lookupLatestCaseSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const {
      treeObjectId,
      folderId,
      folderName,
      trashRetentionDays,
      oldFolderPath,
      movedToTrashByName,
      movedToTrashById,
    } = payload;
    try {
      const { enableDeleteNonEmptyFolder } = selectFeatureFlags(getState());
      // VE-21114: v2 authorizes createFolderContentTemplate (the folder↔SDO
      // link) via OLP against the folder id; the deprecated treeObjectId returns
      // not_allowed. Reads still accept either, so swap the id under v2 only.
      const isV2Folders = selectV2FoldersEnabled(getState());

      if (!enableDeleteNonEmptyFolder) {
        const res = await callGraphQLApi<FetchSubFoldersQueryResponse>({
          actionTypes: [
            CHECK_EMPTY_FOLDER_REQUEST,
            CHECK_EMPTY_FOLDER_SUCCESS,
            CHECK_EMPTY_FOLDER_FAILURE,
          ],
          query: fetchSubFoldersQuery,
          variables: {
            folderId: treeObjectId,
            limit: 1,
            offset: 0,
          },
          dispatch,
          getState,
          throwOnError: false,
          // Selects nested contentTemplates { sdo { data } }; tolerate a
          // dangling (deleted) SDO so one bad child can't collapse this read to
          // undefined and make a non-empty folder look empty (VE-26016).
          returnPartialDataOnNotFound: true,
        });

        if (
          res?.folder?.childFolders?.count &&
          res.folder.childFolders.count > 0
        ) {
          dispatch(
            enqueueSnackbar({
              message: `Folder ${folderName} must be empty to delete!`,
              variant: 'error',
            })
          );
          return;
        }
      }

      const {
        createStructuredData: { id: newSdoId },
      } = await callGraphQLApi<CreateSoftDeletedSdoQueryResponse>({
        actionTypes: [DELETE_FOLDER_INIT, NOOP, NOOP],
        query: createSoftDeletedFolderSdoQuery,
        variables: {
          folderName,
          treeObjectId,
          currentTime: new Date().toISOString(),
          schemaId: schemaId,
          trashRetentionDays,
          oldFolderPath,
          movedToTrashByName,
          movedToTrashById,
        },
        dispatch,
        getState,
        throwOnError: true,
      });

      /* Link the folder and the case sdo together */
      const response = await callGraphQLApi({
        actionTypes: [
          LINK_FOLDER_AND_CASE_SDO,
          LINK_FOLDER_AND_CASE_SDO_SUCCESS,
          LINK_FOLDER_AND_CASE_SDO_FAILURE,
        ],
        query: createNewFolderLinkQuery,
        variables: {
          schemaId,
          folderId: resolveFolderMutationId(isV2Folders, {
            folderId,
            treeObjectId,
          }),
          sdoId: newSdoId,
        },
        dispatch,
        getState,
        throwOnError: true,
      });

      if (response) {
        // VE-21114: the caller normalises oldFolderPath to the v1 (self-inclusive)
        // shape via tdoFolderChain, so the parent is always the second-to-last
        // node in both v1 and v2.
        const oldParentId = oldFolderPath?.at(-2)?.id;
        dispatch(
          actionMoveFolderRequest({
            treeObjectId,
            oldParentTreeId: oldParentId as FolderId,
            newParentTreeId: trashFolderId as FolderId,
            // VE-21114: v2 moveFolder authorizes by folder ids.
            folderId,
            oldParentId: oldParentId as FolderId,
            newParentId: trashFolderId as FolderId,
            isRoot: false,
            suppressSnackbar: true,
          })
        );
        dispatch(SOFT_DELETE_FOLDER_SUCCESS({ id: treeObjectId, folderName }));
      }
    } catch (err) {
      console.error('failed to softDeleteFolder', err, payload);
      dispatch(SOFT_DELETE_FOLDER_FAILURE({ id: treeObjectId, folderName }));
    }
  };

export const deleteMediaFileSdo =
  (payload: ActionDeleteMediaFileSdoPayload) =>
  async (dispatch: Dispatch, getState: () => any) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const { sdoId, isSoftDelete, data } = payload;
    try {
      const deleteResponse = await callGraphQLApi<
        DeleteSdoQueryResponse | SoftDeleteSdoQueryResponse
      >({
        actionTypes: [NOOP, NOOP, DELETE_MEDIA_FILE_SDO_FAILURE],
        query: isSoftDelete ? softDeleteSdoQuery : deleteSdoQuery,
        variables: isSoftDelete
          ? { sdoId, schemaId, data }
          : { sdoId, schemaId },
        dispatch,
        getState,
        throwOnError: true,
      });

      if (deleteResponse) {
        dispatch(DELETE_MEDIA_FILE_SDO_SUCCESS(payload));
      }
    } catch (err) {
      console.error('failed to deleteMediaFileSdo', err, payload);
      dispatch(
        // @ts-expect-error TODO: Validate the type of err matches what's expected here
        DELETE_MEDIA_FILE_SDO_FAILURE([err], {
          variables: { tdoName: payload.tdoName },
        })
      );
    }
  };

export type FetchTdoDetailForSdosResponse = Record<
  string,
  {
    id: string;
    name: string;
    details: unknown;
    jobs: {
      records: {
        status: string;
      }[];
    };
    folders: {
      id: string;
      name: string;
    }[];
  }
>;

/**
 * VE-26943: aliases per request. The export flow settled on 20 for the same
 * shape of query; this selection set is heavier (five asset sub-selections per
 * TDO), so do not raise it without measuring.
 */
export const UNASSIGNED_TDO_DETAIL_BATCH_SIZE = 20;

export const fetchTdoDetailForSdos: Thunk<{
  tdoIds: TDOId[];
}> = (payload) => async (dispatch, getState) => {
  const { tdoIds } = payload;
  // VE-26943: this used to fetch name/details/jobs/folders only — enough for the
  // old panel's name + status rows, and the reason unassigned files could not
  // show a Redacted/Original badge, engine icons, duration or any download
  // option. It now pulls the full per-file selection set (see
  // getUnassignedTdoDetailsQuery), so a mapped row is a normal AssetFileType.
  const batches: TDOId[][] = [];
  for (
    let i = 0;
    i < (tdoIds || []).length;
    i += UNASSIGNED_TDO_DETAIL_BATCH_SIZE
  ) {
    batches.push(tdoIds.slice(i, i + UNASSIGNED_TDO_DETAIL_BATCH_SIZE));
  }

  // Each batch dispatches its own SUCCESS with a partial payload; the reducer
  // merges by tdoId and leaves rows it doesn't mention alone.
  const responses = await Promise.all(
    batches.map((batch) =>
      callGraphQLApi<FetchTdoDetailForSdosResponse>({
        actionTypes: [
          NOOP,
          FETCH_TDO_DETAIL_FOR_SDOS_SUCCESS,
          FETCH_TDO_DETAIL_FOR_SDOS_FAILURE,
        ],
        query: getUnassignedTdoDetailsQuery(batch),
        // VE-27388: the ids are ALIASES baked into the query text, so the
        // operation declares no variables and the server ignores these. They
        // are passed purely so callGraphQLApi puts them on the dispatched
        // action's `meta.variables`, which is the only way the reducer can
        // know WHICH batch just settled. Without them
        // markLegacyTdoDetailAttempted recorded nothing, isEnrichmentOutstanding
        // stayed true for every row forever, and isFetchingLegacyMedias never
        // cleared — a permanent spinner at the foot of the docked list.
        variables: { tdoIds: batch },
        dispatch,
        getState,
        // VE-26943: a TDO deleted out from under its SDO would otherwise fail
        // the whole batch, leaving every other file in it nameless.
        returnPartialDataOnNotFound: true,
      })
    )
  );
  return responses[0];
};

export const callIfTdoIsUnlocked: Thunk<{
  tdoId: string;
  call: () => (dispatch: Dispatch, getState: () => any) => Promise<unknown>;
}> =
  ({ tdoId, call }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestTdoLockSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const oauthToken = selectOAuthToken(getState());
    const sessionToken = selectSessionToken(getState());
    const { apiRoot, graphQLEndpoint, veritoneAppId } =
      getConfig<Window['config']>(getState());
    const authorization = {
      endpoint: `${apiRoot}/${graphQLEndpoint}`,
      token: sessionToken || oauthToken,
      veritoneAppId,
    };

    const tdoLockData = await fetchGraphQLApi<{
      structuredDataObjects: { records: [{ data: UpdateTDOLockResponse }] };
    }>({
      ...authorization,
      query: findTdoLockQuery,
      variables: { schemaId, tdoId },
    });
    const records = tdoLockData?.data?.structuredDataObjects?.records;
    const record = records?.find(({ data: { lastAccessed } }) => {
      const lockedTime = moment(lastAccessed);
      const currentTime = moment();
      const timeDifference = moment
        .duration(currentTime.diff(lockedTime))
        .asMilliseconds();

      const isExpired = timeDifference > LOCK_REFRESH * 5;
      return !isExpired;
    });
    const tdoLock = record?.data;

    if (tdoLock) {
      const { lastAccessed } = tdoLock;
      const lockedTime = moment(lastAccessed);
      const currentTime = moment(new Date().toISOString());
      const timeDifference = moment
        .duration(currentTime.diff(lockedTime))
        .asMilliseconds();

      const { userId } = selectUser(getState());
      const isOwnedByMe = userId === tdoLock.userId;

      if (!isOwnedByMe && timeDifference < LOCK_REFRESH * 5) {
        dispatch(
          TDO_IN_USE({
            messageId: 'cannotDeleteFileInUse',
          })
        );
        return;
      }
    }

    return await call()(dispatch, getState);
  };

export const callIfFolderCaseIsUnlocked: Thunk<{
  treeObjectId: TreeObjectId;
  isCase: boolean;
  call: () => (dispatch: Dispatch, getState: () => any) => Promise<unknown>;
}> =
  ({ treeObjectId, isCase, call }) =>
  async (dispatch, getState) => {
    const defaultLimit = 1000;
    const defaultOffset = 0;
    const schemaId = await lookupLatestTdoLockSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const oauthToken = selectOAuthToken(getState());
    const sessionToken = selectSessionToken(getState());
    const { apiRoot, graphQLEndpoint, veritoneAppId } =
      getConfig<Window['config']>(getState());

    const authorization = {
      endpoint: `${apiRoot}/${graphQLEndpoint}`,
      token: sessionToken || oauthToken,
      veritoneAppId,
    };
    const checkTdoIsLocked = async (tdoId: string) => {
      const tdoLockData = await fetchGraphQLApi<{
        searchMedia: { jsondata: { results: [UpdateTDOLockResponse] } };
      }>({
        ...authorization,
        query: findTdoLockQuery,
        variables: { schemaId, tdoId },
      });

      const tdoLock = tdoLockData?.data?.searchMedia?.jsondata?.results?.[0];
      if (tdoLock) {
        const { lastAccessed } = tdoLock;
        const lockedTime = moment(lastAccessed);
        const currentTime = moment(new Date().toISOString());
        const timeDifference = moment
          .duration(currentTime.diff(lockedTime))
          .asMilliseconds();

        const { userId } = selectUser(getState());
        const isOwnedByMe = userId === tdoLock.userId;

        if (!isOwnedByMe && timeDifference < LOCK_REFRESH * 5) {
          return true;
        }
      }
      return false;
    };

    const checkCaseIsLocked = async (
      id: string
    ): Promise<{ isLocked: boolean; isJobProcessing: boolean }> => {
      const result = { isLocked: false, isJobProcessing: false };
      const checkFiles = async (
        offset: number
      ): Promise<{ isLocked: boolean; isJobProcessing: boolean }> => {
        const folderData = await fetchGraphQLApi<{
          folder: Partial<FolderStateType>;
        }>({
          ...authorization,
          query: getFilesByCaseIdQuery,
          variables: { caseId: id, limit: defaultLimit, offset },
        });

        const tdos = folderData?.data?.folder?.childTDOs?.records || [];
        if (tdos && tdos.length > 0) {
          for (const tdo of tdos) {
            if (tdo) {
              const status = tdo.jobs?.records?.[0]?.status;
              const activeJobsStatus = [JobStatus.Pending, JobStatus.Running];
              if (status && activeJobsStatus.includes(status)) {
                result.isJobProcessing = true;
                return result;
              }
              const tdoIsLocked = await checkTdoIsLocked(tdo.id);
              if (tdoIsLocked) {
                result.isLocked = true;
                return result;
              }
            }
          }
          if (tdos.length === defaultLimit) {
            return await checkFiles(offset + defaultLimit);
          }
        }
        return result;
      };
      return await checkFiles(defaultOffset);
    };

    const checkFolderIsLocked = async (
      id: string
    ): Promise<{ isLocked: boolean; isJobProcessing: boolean }> => {
      const result = { isLocked: false, isJobProcessing: false };
      const getChildFolders = async (
        offset: number
      ): Promise<{ isLocked: boolean; isJobProcessing: boolean }> => {
        const folderData = await fetchGraphQLApi<{
          folder: Partial<FolderStateType>;
        }>({
          ...authorization,
          query: getChildFoldersQuery,
          variables: { folderId: id, limit: defaultLimit, offset },
        });

        const folders = folderData?.data?.folder?.childFolders?.records || [];
        if (folders && folders.length > 0) {
          const caseIds = (
            folders.filter((f) => (f?.contentTemplates?.length || 0) > 0) || []
          ).map((f) => f.id);

          if (caseIds?.length > 0) {
            for (const caseId of caseIds) {
              if (caseId) {
                const caseLockResponse = await checkCaseIsLocked(caseId);
                if (
                  caseLockResponse.isLocked ||
                  caseLockResponse.isJobProcessing
                ) {
                  return caseLockResponse;
                }
              }
            }
          }

          const folderOnlyIds = (
            folders.filter(
              (f) => !(((f.id && f.contentTemplates?.length) || 0) > 0)
            ) || []
          ).map((f) => f.id);

          if ((folderOnlyIds?.length || 0) > 0) {
            for (const folderId of folderOnlyIds) {
              if (folderId) {
                const folderLockResponse = await checkFolderIsLocked(folderId);
                if (
                  folderLockResponse.isJobProcessing ||
                  folderLockResponse.isLocked
                ) {
                  return folderLockResponse;
                }
              }
            }
          }

          if (folders?.length === defaultLimit) {
            return await getChildFolders(offset + defaultOffset);
          }
        }
        return result;
      };
      return await getChildFolders(defaultOffset);
    };

    let lockResult = { isLocked: false, isJobProcessing: false };
    if (isCase) {
      lockResult = await checkCaseIsLocked(treeObjectId);
    } else {
      lockResult = await checkFolderIsLocked(treeObjectId);
    }
    if (lockResult.isLocked || lockResult.isJobProcessing) {
      dispatch(
        TDO_IN_USE({
          messageId: lockResult.isLocked
            ? 'cannotDeleteFileInUse'
            : 'cannotDeleteFileJobsInProcessing',
        })
      );
      return;
    } else {
      return await call()(dispatch, getState);
    }
  };

export const deleteFolderCaseAndFiles =
  <T = any>(
    query: string,
    variables: {
      id: TreeObjectId;
      folderName: string;
      isPermanentDelete?: boolean;
    },
    actionTypes: Head<Parameters<typeof callGraphQLApi<T>>>['actionTypes']
  ) =>
  async (dispatch: Dispatch, getState: () => any) => {
    const { enableDeleteNonEmptyFolder, v2FoldersEnabled } =
      selectFeatureFlags(getState());
    if (!variables.isPermanentDelete && !enableDeleteNonEmptyFolder) {
      const res = await callGraphQLApi<FetchSubFoldersQueryResponse>({
        actionTypes: [
          CHECK_EMPTY_FOLDER_REQUEST,
          CHECK_EMPTY_FOLDER_SUCCESS,
          CHECK_EMPTY_FOLDER_FAILURE,
        ],
        query: fetchSubFoldersQuery,
        variables: {
          folderId: variables.id,
          limit: 1,
          offset: 0,
        },
        dispatch,
        getState,
        throwOnError: false,
        // Selects nested contentTemplates { sdo { data } }; tolerate a dangling
        // (deleted) SDO so one bad child can't collapse this read to undefined
        // and make a non-empty folder look empty (VE-26016).
        returnPartialDataOnNotFound: true,
      });

      // VE-21114: `childFolders.count` misses files attached to a case. Under
      // v2's hard delete, also count files so a non-empty case is blocked with a
      // clear message instead of an opaque API failure. v2-gated to keep the v1
      // cascade-delete behavior unchanged.
      let hasFiles = false;
      if (v2FoldersEnabled) {
        const filesRes = await callGraphQLApi<FetchCaseContentsQueryResponse>({
          actionTypes: [NOOP, NOOP, NOOP],
          query: fetchCaseContentsQuery,
          variables: { caseId: variables.id, limit: 1, offset: 0 },
          dispatch,
          getState,
          throwOnError: false,
        });
        hasFiles = (filesRes?.folder?.childTDOs?.count ?? 0) > 0;
      }

      if (
        (res?.folder?.childFolders?.count &&
          res.folder.childFolders.count > 0) ||
        hasFiles
      ) {
        dispatch(
          enqueueSnackbar({
            message: `Folder ${variables.folderName} must be empty to delete!`,
            variant: 'error',
          })
        );
        return;
      }
    }
    const defaultLimit = 1000;
    const defaultOffset = 0;
    let tdoIds: Array<string> = [];
    let isDeleteFilesSuccess = false;
    const getFiles = async (offset: number): Promise<boolean> => {
      try {
        const result = await callGraphQLApi<FetchCaseContentsQueryResponse>({
          actionTypes: [NOOP, NOOP, NOOP],
          query: fetchCaseContentsQuery,
          variables: {
            caseId: variables.id,
            limit: 1000,
            offset,
          },
          dispatch,
          getState,
          throwOnError: false,
        });
        if (result) {
          const newTdos = (result.folder.childTDOs.records || []).map(
            (r) => r.id
          );
          if (arrayHasContents(newTdos)) {
            tdoIds = uniq([...tdoIds, ...newTdos]);
            if (newTdos.length === defaultLimit) {
              return await getFiles(offset + defaultLimit);
            } else {
              return true;
            }
          } else {
            return true;
          }
        }
      } catch (err) {
        console.log(err);
        return false;
      }
      return true;
    };
    const isSuccess = await getFiles(defaultOffset);
    if (isSuccess) {
      if (tdoIds.length > 0) {
        const deleteFilesQuery = `mutation deleteTDOs {
          ${tdoIds
            .map(
              (tdoId, index) => `
              i${index}: deleteTDO(id: "${tdoId}") {
                id
              }`
            )
            .join(',')}
        }`;
        try {
          const deletedIds = await callGraphQLApi({
            actionTypes: [NOOP, NOOP, NOOP],
            query: deleteFilesQuery,
            variables: {},
            dispatch,
            getState,
            throwOnError: false,
          });
          console.log('deletedIds: ' + JSON.stringify(deletedIds));
          isDeleteFilesSuccess = true;
          // VE-28551: deleteTDO cascades to none of a file's structured data,
          // and deleting a request deletes every file inside it — so without
          // this, emptying one request strands the side data of all of them at
          // once. Best-effort and after the fact, like every other purge.
          //
          // Purge only what the server said it deleted, NOT what we asked it
          // to. A single `not_found` in the batch makes callGraphQLApi return
          // undefined WITHOUT throwing (see its errorNotFoundAfterFailure
          // branch), so a stale listing holding one already-deleted file and
          // one the caller may not delete would otherwise hard-delete the
          // second file's history and comments while the file itself lives on.
          purgeSideDataForDeletedTdos(dispatch, deletedIds);
        } catch (err) {
          console.log(err);
          isDeleteFilesSuccess = false;
        }
      } else {
        isDeleteFilesSuccess = true;
      }
      if (isDeleteFilesSuccess) {
        await callGraphQLApi<T>({
          actionTypes,
          query,
          variables,
          dispatch,
          getState,
        });
      } else {
        dispatch(DELETE_FOLDER_FAILURE(undefined, { variables }));
      }
    }
  };

export const deleteFolderAndSubfolders =
  <T = any>(
    query: string,
    variables: {
      id: TreeObjectId;
      folderName: string;
      isPermanentDelete?: boolean;
    },
    actionTypes: Head<Parameters<typeof callGraphQLApi<T>>>['actionTypes']
  ) =>
  async (dispatch: Dispatch, getState: () => any) => {
    const defaultLimit = 1000;
    const defaultOffset = 0;
    // VE-21114: under v2 the deleteFolder mutation must reference the folder id
    // (OLP), not the deprecated treeObjectId. Reads (child fetch) still accept
    // treeObjectId, so only the delete-node id switches.
    const { v2FoldersEnabled: isV2Folders } = selectFeatureFlags(getState());

    type ChildFolderRecord =
      FetchSubFoldersQueryResponse['folder']['childFolders']['records'][number];

    // Lazily resolve the case schema id only when we actually find a
    // case in the tree, so we don't pay for the lookup on pure folder
    // hierarchies. The result is cached for subsequent cases.
    let caseSchemaIdCache: string | null | undefined;
    const getCaseSchemaId = async (): Promise<string | null> => {
      if (caseSchemaIdCache !== undefined) {
        return caseSchemaIdCache;
      }
      const id = await lookupLatestCaseSchemaId(dispatch, getState);
      caseSchemaIdCache = id || null;
      return caseSchemaIdCache;
    };

    const fetchAllChildFolders = async (
      folderId: TreeObjectId
    ): Promise<ChildFolderRecord[] | null> => {
      const records: ChildFolderRecord[] = [];
      const fetchPage = async (offset: number): Promise<boolean> => {
        try {
          const result = await callGraphQLApi<FetchSubFoldersQueryResponse>({
            actionTypes: [NOOP, NOOP, NOOP],
            query: fetchSubFoldersQuery,
            variables: { folderId, limit: defaultLimit, offset },
            dispatch,
            getState,
            throwOnError: false,
            // Selects nested contentTemplates { sdo { data } }; tolerate a
            // dangling (deleted) SDO so one bad child can't collapse this read
            // to undefined and silently truncate the subtree walk (VE-26016).
            returnPartialDataOnNotFound: true,
          });
          const childRecords = result?.folder?.childFolders?.records;
          if (arrayHasContents(childRecords)) {
            records.push(...childRecords);
            if (childRecords.length === defaultLimit) {
              return await fetchPage(offset + defaultLimit);
            }
          }
          return true;
        } catch (err) {
          console.error('Failed to fetch child folders for', folderId, err);
          return false;
        }
      };
      const ok = await fetchPage(defaultOffset);
      return ok ? records : null;
    };

    const deleteCaseTdos = async (caseId: TreeObjectId): Promise<boolean> => {
      let tdoIds: string[] = [];
      const collectTdos = async (offset: number): Promise<boolean> => {
        try {
          const result = await callGraphQLApi<FetchCaseContentsQueryResponse>({
            actionTypes: [NOOP, NOOP, NOOP],
            query: fetchCaseContentsQuery,
            variables: { caseId, limit: defaultLimit, offset },
            dispatch,
            getState,
            throwOnError: false,
          });
          if (result) {
            const newTdos = (result.folder.childTDOs.records || []).map(
              (r) => r.id
            );
            if (arrayHasContents(newTdos)) {
              tdoIds = uniq([...tdoIds, ...newTdos]);
              if (newTdos.length === defaultLimit) {
                return await collectTdos(offset + defaultLimit);
              }
            }
          }
          return true;
        } catch (err) {
          console.error('Failed to fetch TDOs for case', caseId, err);
          return false;
        }
      };

      const collected = await collectTdos(defaultOffset);
      if (!collected) {
        return false;
      }
      if (tdoIds.length === 0) {
        return true;
      }
      const deleteFilesQuery = `mutation deleteTDOs {
        ${tdoIds
          .map(
            (tdoId, index) => `
            i${index}: deleteTDO(id: "${tdoId}") {
              id
            }`
          )
          .join(',')}
      }`;
      try {
        const deletedIds = await callGraphQLApi({
          actionTypes: [NOOP, NOOP, NOOP],
          query: deleteFilesQuery,
          variables: {},
          dispatch,
          getState,
          throwOnError: false,
        });
        // VE-28551: same as the case path above — the files are gone, their
        // per-TDO SDOs are not, and nothing else will come back for them. And
        // the same caveat: only what the server reported deleted.
        purgeSideDataForDeletedTdos(dispatch, deletedIds);
        return true;
      } catch (err) {
        console.error('Failed to delete TDOs for case', caseId, err);
        return false;
      }
    };

    const deleteFolderNode = async (
      folderId: string, // VE-21114: treeObjectId (v1) or folder id (v2)
      folderName: string
    ): Promise<boolean> => {
      try {
        await callGraphQLApi({
          actionTypes: [NOOP, NOOP, NOOP],
          query: deleteFolderOrCaseQuery,
          variables: { id: folderId, folderName },
          dispatch,
          getState,
          throwOnError: true,
        });
        return true;
      } catch (err) {
        console.error('Failed to delete folder', folderId, err);
        return false;
      }
    };

    // Remove every structured-data object attached to a case folder.
    // Mirrors the behaviour of `deleteCaseSdo` which is what the UI
    // dispatches when a case is deleted directly.
    const deleteCaseSdos = async (
      record: ChildFolderRecord
    ): Promise<boolean> => {
      const contentTemplates = (record.contentTemplates || []).filter(
        (contentTemplate) => !!contentTemplate?.sdoId
      );
      if (contentTemplates.length === 0) {
        return true;
      }
      const schemaId = await getCaseSchemaId();
      if (!schemaId) {
        console.error(
          'Failed to resolve case schema id while deleting',
          record.id
        );
        return false;
      }
      for (const contentTemplate of contentTemplates) {
        try {
          const response = await callGraphQLApi<DeleteSdoQueryResponse>({
            actionTypes: [NOOP, NOOP, NOOP],
            query: deleteSdoQuery,
            variables: { sdoId: contentTemplate.sdoId, schemaId },
            dispatch,
            getState,
            throwOnError: true,
          });
          if (response?.deleteStructuredData?.id !== contentTemplate.sdoId) {
            console.error(
              'Unexpected response when deleting SDO',
              contentTemplate.sdoId,
              response
            );
            return false;
          }
        } catch (err) {
          console.error('Failed to delete SDO', contentTemplate.sdoId, err);
          return false;
        }
      }
      return true;
    };

    const isCaseRecord = (record: ChildFolderRecord) =>
      (record?.contentTemplates?.length || 0) > 0;

    const deleteSubtree = async (
      record: ChildFolderRecord
    ): Promise<boolean> => {
      const childId = record.treeObjectId;
      // Reads accept treeObjectId under v2; the delete mutation needs the id.
      const deleteId = resolveFolderMutationId(isV2Folders, {
        folderId: record.id,
        treeObjectId: record.treeObjectId,
      });
      if (isCaseRecord(record)) {
        const sdosDeleted = await deleteCaseSdos(record);
        if (!sdosDeleted) {
          return false;
        }
        const tdosDeleted = await deleteCaseTdos(childId);
        if (!tdosDeleted) {
          return false;
        }
        return await deleteFolderNode(deleteId, record.name);
      }

      const grandChildren = await fetchAllChildFolders(childId);
      if (grandChildren === null) {
        return false;
      }
      for (const grandChild of grandChildren) {
        const ok = await deleteSubtree(grandChild);
        if (!ok) {
          return false;
        }
      }
      return await deleteFolderNode(deleteId, record.name);
    };

    const { enableDeleteNonEmptyFolder } = selectFeatureFlags(getState());
    if (!variables.isPermanentDelete && !enableDeleteNonEmptyFolder) {
      const res = await callGraphQLApi<FetchSubFoldersQueryResponse>({
        actionTypes: [
          CHECK_EMPTY_FOLDER_REQUEST,
          CHECK_EMPTY_FOLDER_SUCCESS,
          CHECK_EMPTY_FOLDER_FAILURE,
        ],
        query: fetchSubFoldersQuery,
        variables: {
          folderId: variables.id,
          limit: 1,
          offset: 0,
        },
        dispatch,
        getState,
        throwOnError: false,
        // Selects nested contentTemplates { sdo { data } }; tolerate a dangling
        // (deleted) SDO so one bad child can't collapse this read to undefined
        // and make a non-empty folder look empty (VE-26016).
        returnPartialDataOnNotFound: true,
      });

      if (
        res?.folder?.childFolders?.count &&
        res.folder.childFolders.count > 0
      ) {
        dispatch(
          enqueueSnackbar({
            message: `Folder ${variables.folderName} must be empty to delete!`,
            variant: 'error',
          })
        );
        return;
      }
    }

    const topLevelChildren = await fetchAllChildFolders(variables.id);
    if (topLevelChildren === null) {
      dispatch(DELETE_FOLDER_FAILURE(undefined, { variables }));
      return;
    }

    for (const child of topLevelChildren) {
      const ok = await deleteSubtree(child);
      if (!ok) {
        dispatch(DELETE_FOLDER_FAILURE(undefined, { variables }));
        return;
      }
    }

    await callGraphQLApi<T>({
      actionTypes,
      query,
      variables,
      dispatch,
      getState,
    });
  };

/**
 * VE-28061: the same output-asset read as `fetchStalenessAssets`, but routed
 * through the action lifecycle so the reducer can patch the rows in place
 * (REFRESH_FILE_OUTPUTS_SUCCESS). Partial-data tolerant: one purged TDO must not
 * blank the refresh for the others.
 */
export const fetchFileOutputs = (tdoIds: NonEmptyArray<TDOId>) =>
  invokeGqlCall<StalenessAssetsResponse>(
    fetchStalenessAssetsQuery(tdoIds),
    // The query aliases the ids inline rather than binding them, so the server
    // ignores these. Passed anyway because callGraphQLApi attaches request
    // variables to the failure action's meta, which is how the retry in
    // `refreshFileOutputsFailure` recovers which files to ask for again.
    { tdoIds },
    [
      REFRESH_FILE_OUTPUTS_INIT,
      REFRESH_FILE_OUTPUTS_SUCCESS,
      REFRESH_FILE_OUTPUTS_FAILURE,
    ],
    { returnPartialDataOnNotFound: true }
  );

/**
 * Fetch one TDO's `media` assets, so a download can pick between the ingested
 * file and the searchable copy the OCR engine republished.
 *
 * Returns `[]` on failure rather than throwing: the caller falls back to the
 * asset the folder already loaded, which is the pre-existing behaviour and a
 * working download, rather than nothing happening when the user clicks.
 */
export const fetchMediaAssets =
  (tdoId: TDOId) =>
  async (
    dispatch: Dispatch,
    getState: () => any
  ): Promise<{
    media: MediaAssetRecord[];
    originalFormat: MediaAssetRecord[];
  }> => {
    try {
      const response = await callGraphQLApi<MediaAssetsResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: fetchMediaAssetsQuery,
        variables: { id: tdoId },
        dispatch,
        getState,
        throwOnError: true,
      });
      return {
        media: response?.temporalDataObject?.assets?.records ?? [],
        originalFormat:
          response?.temporalDataObject?.originalFormatAssets?.records ?? [],
      };
    } catch (err) {
      console.warn(
        'Media asset fetch failed; downloading the loaded asset instead',
        err
      );
      return { media: [], originalFormat: [] };
    }
  };

/**
 * Fetch the assets a staleness verdict needs, keyed by TDO id (VE-25870).
 *
 * Called when the user asks to download, not when the folder opens: the verdict
 * should reflect what is true at download time, and browsing shouldn't pay for
 * it. Returns `{}` on failure — the caller then judges from what the row already
 * has, which is the pre-existing behaviour rather than a blocked download.
 */
export const fetchStalenessAssets =
  (tdoIds: TDOId[]) =>
  async (
    dispatch: Dispatch,
    getState: () => any
  ): Promise<Record<string, StalenessAssetsResponse[string]>> => {
    if (!arrayHasContents(tdoIds)) {
      return {};
    }
    try {
      const response = await callGraphQLApi<StalenessAssetsResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: fetchStalenessAssetsQuery(tdoIds),
        variables: {},
        dispatch,
        getState,
        throwOnError: true,
      });
      const byTdoId: Record<string, StalenessAssetsResponse[string]> = {};
      for (const value of Object.values(response ?? {})) {
        if (value?.id) {
          byTdoId[value.id] = value;
        }
      }
      return byTdoId;
    } catch (err) {
      console.warn(
        'Staleness asset fetch failed; judging from loaded data',
        err
      );
      return {};
    }
  };
